import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecords, PageHead, Drawer, Text, Area, Pick, Err } from '../ui.js';
import { useData } from '../data.js';
import type { IcpProfile, Persona } from '../api.js';
import { ApiError, draftIcp } from '../api.js';

// ICP and personas on ONE page, as cards, because targeting is a combination
// question: which kind of company, and which person inside it. Two screens force
// you to hold half the answer in your head while you look at the other half.
//
// The wizard replaced a single long form. A twenty-field form asked all at once
// gets abandoned or filled in badly; five short questions get finished.
//
// Personas are not decoration: their titles and seniorities are what the Apollo
// contact search actually queries. A persona that cannot become a query is a
// poster.

type Icp = IcpProfile & {
  id: string; company_type: string | null; business_type: string | null;
  cities: string[]; states: string[]; countries: string[]; pains: string | null;
  industries: string[]; employee_min: number | null; employee_max: number | null;
  revenue_min: number | null; revenue_max: number | null; is_default: boolean;
  summary: string | null; exclusions: string[];
};
type Per = Persona & {
  id: string; icp_id: string | null; titles: string[]; seniorities: string[];
  responsibilities: string | null; pains: string | null; role_kind: string | null;
  message_angle: string | null;
};

const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);
const csv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
const money = (n: number | null) =>
  n === null ? '—' : n >= 1_000_000 ? `$${n / 1_000_000}M` : `$${(n / 1000).toFixed(0)}K`;

const ROLES = ['decision_maker', 'economic_buyer', 'champion', 'influencer', 'blocker', 'user'];

export function Icp() {
  const icps = useRecords<Icp>('icp_profiles');
  const personas = useRecords<Per>('personas');
  const [wizard, setWizard] = useState(false);
  const [seed, setSeed] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draftErr, setDraftErr] = useState<string | null>(null);
  const [gaps, setGaps] = useState<string | null>(null);
  const { companies } = useData();
  const [addingPersona, setAddingPersona] = useState(false);
  const [editing, setEditing] = useState<Per | null>(null);

  // The targeting selection — an ICP plus the people inside it. This is what
  // gets handed to research, and the only reason both live on one page.
  const [pickedIcp, setPickedIcp] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const nav = useNavigate();

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // "Is this the first ICP" is not the same question as "is the default seat
  // empty": a workspace can hold profiles and no default at all — deleting the
  // default one through /api/records leaves exactly that. Asked the old way,
  // this wizard kept adding non-default profiles to a workspace that had
  // nothing for fit-core.ts to score accounts against.
  const noDefaultYet = !(icps.rows ?? []).some((i) => i.is_default);

  const icp = icps.rows?.find((i) => i.id === pickedIcp) ?? null;
  const chosen = (personas.rows ?? []).filter((p) => picked.has(p.id));
  const titleCount = new Set(chosen.flatMap((p) => list(p.titles))).size;

  return (
    <div className="icp-page">
      <PageHead
        title="ICP & personas"
        sub="Who you sell to, and who inside it you talk to"
        action={
          (icps.rows?.length ?? 0) > 0
            ? <button className="primary" onClick={() => setWizard(true)}>Add an ICP</button>
            : null
        }
      />

      {/* THE BLANK SLATE. Fourteen fields across five steps is a fine way to
          EDIT an ICP and a terrible way to make the first one — nothing has
          happened yet, so every field is a cost with no visible return. Nobody
          holds their ICP as fourteen fields anyway; they hold it as a sentence,
          and decomposing a sentence into fields is a machine's job. */}
      {icps.rows?.length === 0 && (
        <div className="blankicp">
          <h3>Who do you sell to?</h3>
          <p className="meta">
            A sentence or two is enough. This drafts the profile and the buying
            committee — titles, pains, disqualifiers — and you correct it from there.
            Everything downstream reads it: research, contact search, scoring, and
            the cadence recommender.
          </p>
          <textarea
            rows={4}
            value={seed}
            placeholder="Mid-market construction software companies, roughly $5M to $150M, sales-led, where marketing leadership has stalled or never existed."
            onChange={(e) => setSeed(e.target.value)}
          />
          <div className="actions">
            <button className="primary" disabled={drafting || seed.trim().length < 15}
              onClick={async () => {
                setDrafting(true); setDraftErr(null);
                try {
                  const r = await draftIcp(seed.trim());
                  setGaps(r.gaps); setSeed('');
                  void icps.reload(); void personas.reload();
                } catch (e) {
                  setDraftErr(e instanceof ApiError ? e.message : String(e));
                } finally { setDrafting(false); }
              }}>
              {drafting ? 'Drafting…' : 'Draft it'}
            </button>
            <button onClick={() => setWizard(true)}>Fill it in myself</button>
          </div>
          <Err>{draftErr}</Err>
        </div>
      )}

      {/* A draft that hides its own holes is worse than a short one. */}
      {gaps && (
        <p className="warnbox">
          <strong>Left blank, because you did not say:</strong> {gaps}
        </p>
      )}

      {/* MASTER–DETAIL, NOT TWO WALLS.
          The old screen stacked a grid of large ICP cards on top of a flat grid
          of personas. Two faults. The cards read in full — summary, five facts,
          a pains paragraph each — so three profiles were a wall before you
          reached the people. And a flat persona grid below implies personas are
          peers of profiles when they hang off one, which also left "Add persona"
          unable to say which profile it would attach to.
          A profile is now one selectable row that expands in place, with its own
          personas inside it. Hierarchy drawn rather than described, and the row
          you expand IS the one you are targeting. */}
      {(icps.rows?.length ?? 0) > 0 && (
        <div className="icplist">
          {icps.rows?.map((i) => {
            const on = pickedIcp === i.id;
            const mine = (personas.rows ?? []).filter((p) => !p.icp_id || p.icp_id === i.id);
            const scored = (companies ?? [])
              .filter((x) => (x as unknown as { icp_id?: string }).icp_id === i.id).length;
            return (
              <section key={i.id} className={`icprow ${on ? 'open' : ''}`}>
                <button className="icprow-head"
                  onClick={() => setPickedIcp(on ? null : i.id)} aria-expanded={on}>
                  <span className="fold-caret" aria-hidden="true">{on ? '▾' : '▸'}</span>
                  <span className="icprow-name">{i.name}</span>
                  {i.is_default && <span className="flag flag-ok">default</span>}
                  <span className="icprow-sum">
                    {scored} account{scored === 1 ? '' : 's'} · {mine.length} persona
                    {mine.length === 1 ? '' : 's'}
                    {i.summary ? ` · ${i.summary.slice(0, 70)}${i.summary.length > 70 ? '…' : ''}` : ''}
                  </span>
                </button>

                {on && (
                  <div className="icprow-body">
                    <dl className="facts">
                      {i.company_type && <><dt>Type</dt><dd>{i.company_type}</dd></>}
                      {list(i.industries).length > 0 && (
                        <><dt>Industry</dt><dd>{list(i.industries).join(', ')}</dd></>
                      )}
                      {(i.employee_min || i.employee_max) && (
                        <><dt>Size</dt><dd>{i.employee_min ?? '—'}–{i.employee_max ?? '—'} people</dd></>
                      )}
                      {(i.revenue_min || i.revenue_max) && (
                        <><dt>Revenue</dt><dd>{money(i.revenue_min)}–{money(i.revenue_max)}</dd></>
                      )}
                      {[...list(i.cities), ...list(i.states), ...list(i.countries)].length > 0 && (
                        <>
                          <dt>Where</dt>
                          <dd>{[...list(i.cities), ...list(i.states), ...list(i.countries)].join(' · ')}</dd>
                        </>
                      )}
                    </dl>
                    <SeedRow icp={i} onSave={(d) => icps.update(i.id, d)} />
                    {i.pains && <p className="painnote"><strong>Needs / pains.</strong> {i.pains}</p>}
                    {list(i.exclusions).length > 0 && (
                      <p className="painnote">
                        <strong>Skip.</strong> {list(i.exclusions).join(' · ')}
                      </p>
                    )}

                    <h4 className="icprow-people">
                      Who you talk to inside it
                      <button className="mini" onClick={() => setAddingPersona(true)}>
                        Add a persona
                      </button>
                    </h4>

                    {mine.length === 0 && (
                      <p className="meta">
                        No personas yet. Without titles there is nothing for the contact
                        search to look for.
                      </p>
                    )}

                    <div className="cardgrid">
                      {mine.map((p) => (
                        <div key={p.id} className={`percard ${picked.has(p.id) ? 'picked' : ''}`}>
                          <button className="percard-body" onClick={() => toggle(p.id)}>
                            <header>
                              <h4>{p.name}</h4>
                              {p.role_kind && (
                                <span className="flag flag-mute">{p.role_kind.replace('_', ' ')}</span>
                              )}
                            </header>
                            <div className="titlechips">
                              {list(p.titles).slice(0, 5).map((t) => (
                                <span className="tchip" key={t}>{t}</span>
                              ))}
                              {list(p.titles).length > 5 && (
                                <span className="tchip more">+{list(p.titles).length - 5} more</span>
                              )}
                            </div>
                            {p.responsibilities && (
                              <p className="perline">
                                <strong>On the hook for.</strong> {p.responsibilities}
                              </p>
                            )}
                            {p.pains && <p className="perline"><strong>Pains.</strong> {p.pains}</p>}
                          </button>
                          <div className="percard-foot">
                            <button className="mini" onClick={() => setEditing(p)}>Edit</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {(icp || chosen.length > 0) && (
        <div className="targetbar">
          <div>
            <strong>Targeting</strong>{' '}
            {icp ? icp.name : <span className="meta">no ICP selected</span>}
            {chosen.length > 0 && (
              <> · {chosen.length} persona{chosen.length > 1 ? 's' : ''} · {titleCount} titles</>
            )}
          </div>
          <div className="targetactions">
            <button onClick={() => { setPickedIcp(null); setPicked(new Set()); }}>Clear</button>
            <button
              className="primary"
              disabled={!icp}
              title={icp ? '' : 'Pick an ICP first — it decides which companies to search for.'}
              onClick={() => {
                const q = new URLSearchParams({ icp: icp!.id });
                if (chosen.length) q.set('personas', chosen.map((p) => p.id).join(','));
                nav(`/research?${q}`);
              }}
            >
              Research this
            </button>
          </div>
        </div>
      )}

      {wizard && (
        <IcpWizard
          noDefaultYet={noDefaultYet}
          onClose={() => setWizard(false)}
          onSave={(d) => icps.create(d)}
        />
      )}
      {(addingPersona || editing) && (
        <PersonaForm
          persona={editing}
          icpId={pickedIcp ?? icps.rows?.find((i) => i.is_default)?.id ?? null}
          onClose={() => { setAddingPersona(false); setEditing(null); }}
          onSave={(d) => (editing ? personas.update(editing.id, d) : personas.create(d))}
        />
      )}
    </div>
  );
}

// LOOKALIKE SEEDS, EDITABLE IN PLACE.
//
// There is no ICP editor in this app — the wizard creates, and after that the
// profile is read-only. Building a full editor to add one field would have been
// the wrong trade, and leaving it wizard-only would mean every workspace that
// already has an ICP (which is all of them) could never name a seed.
//
// So: the one field that needed a way in gets one, and nothing else moves.
function SeedRow({ icp, onSave }: {
  icp: Icp;
  onSave: (d: Record<string, unknown>) => Promise<unknown>;
}) {
  const seeds = list(icp.seed_accounts);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(seeds.join(', '));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true); setError(null);
    try {
      await onSave({ seed_accounts: csv(text) });
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  if (editing) {
    return (
      <div className="seedrow">
        <Err>{error}</Err>
        <Text label="Companies that look like your target" value={text}
          placeholder="Procore, raken.com, Buildertrend"
          onChange={(e) => setText(e.target.value)} />
        <div className="toolbar">
          <button className="mini" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button className="mini" disabled={busy}
            onClick={() => { setText(seeds.join(', ')); setEditing(false); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <p className="painnote seedrow">
      <strong>Looks like.</strong>{' '}
      {seeds.length
        ? seeds.join(' · ')
        : <span className="meta">
            Nothing named yet — the agent is searching from adjectives alone.
          </span>}
      {' '}
      <button className="mini" onClick={() => setEditing(true)}>
        {seeds.length ? 'Edit' : 'Name a few'}
      </button>
    </p>
  );
}

// --- the wizard --------------------------------------------------------------

const STEPS = [
  { key: 'who', title: 'Who are they?', hint: 'The shape of the company.' },
  { key: 'like', title: 'Who looks like them?',
    hint: 'Two or three real companies. The fastest way to say what you mean.' },
  { key: 'size', title: 'How big?', hint: 'Too small cannot afford you. Too big has no gap to fill.' },
  { key: 'where', title: 'Where?', hint: 'Leave a level blank to keep it open.' },
  { key: 'pain', title: 'What is broken?', hint: 'The reason they need you. This is the wedge.' },
  { key: 'out', title: 'Who do you skip?', hint: 'Disqualifiers save more time than qualifiers.' },
] as const;

function IcpWizard({ onClose, onSave, noDefaultYet }: {
  onClose: () => void;
  onSave: (d: Record<string, unknown>) => Promise<unknown>;
  /** Nothing holds the default flag, so this profile should take it. */
  noDefaultYet: boolean;
}) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [f, setF] = useState({
    name: '', summary: '', company_type: '', business_type: '', industries: '',
    employee_min: '', employee_max: '', revenue_min: '', revenue_max: '',
    cities: '', states: '', countries: '', pains: '', exclusions: '',
    seed_accounts: '',
  });
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
  const last = step === STEPS.length - 1;

  async function save() {
    setSaving(true); setError(null);
    try {
      await onSave({
        name: f.name.trim(), summary: f.summary || null,
        company_type: f.company_type || null, business_type: f.business_type || null,
        industries: csv(f.industries),
        employee_min: f.employee_min ? Number(f.employee_min) : null,
        employee_max: f.employee_max ? Number(f.employee_max) : null,
        // Entered in millions because nobody wants to count zeroes.
        revenue_min: f.revenue_min ? Number(f.revenue_min) * 1_000_000 : null,
        revenue_max: f.revenue_max ? Number(f.revenue_max) * 1_000_000 : null,
        cities: csv(f.cities), states: csv(f.states), countries: csv(f.countries),
        pains: f.pains || null, exclusions: csv(f.exclusions),
        seed_accounts: csv(f.seed_accounts),
        is_default: noDefaultYet,
      });
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <Drawer
      title="Define an ICP"
      subtitle={`Step ${step + 1} of ${STEPS.length} — ${STEPS[step].title}`}
      onClose={onClose}
      footer={
        <>
          {step > 0 && <button className="ghost" onClick={() => setStep(step - 1)}>Back</button>}
          {!last && (
            <button className="primary" disabled={step === 0 && !f.name.trim()}
              onClick={() => setStep(step + 1)}>Next</button>
          )}
          {last && (
            <button className="primary" disabled={saving || !f.name.trim()} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save ICP'}
            </button>
          )}
        </>
      }
    >
      <Err>{error}</Err>
      <div className="steps">
        {STEPS.map((s, i) => (
          <span key={s.key} className={`stepdot ${i === step ? 'on' : ''} ${i < step ? 'done' : ''}`} />
        ))}
      </div>
      <p className="meta">{STEPS[step].hint}</p>

      {step === 0 && (
        <>
          <Text label="Name this ICP" value={f.name} autoFocus
            placeholder="Mid-market SaaS — sales-led, marketing gap"
            onChange={(e) => set('name', e.target.value)} />
          <Text label="Company type" value={f.company_type} placeholder="B2B SaaS"
            onChange={(e) => set('company_type', e.target.value)} />
          <Text label="Industry" value={f.industries} hint="Comma separated."
            placeholder="construction software, AEC software"
            onChange={(e) => set('industries', e.target.value)} />
          <Text label="Type of business" value={f.business_type}
            placeholder="Software vendor selling into construction"
            onChange={(e) => set('business_type', e.target.value)} />
          <Area label="One-line summary" rows={3} value={f.summary}
            onChange={(v) => set('summary', v)} />
        </>
      )}

      {step === 1 && (
        <Area label="Companies that look like your target" rows={6} value={f.seed_accounts}
          hint="Comma separated. Names or domains both work."
          placeholder="Procore, raken.com, Buildertrend"
          onChange={(v) => set('seed_accounts', v)} />
      )}

      {step === 2 && (
        <>
          <div className="pair">
            <Text label="Employees — min" type="number" value={f.employee_min}
              onChange={(e) => set('employee_min', e.target.value)} />
            <Text label="Employees — max" type="number" value={f.employee_max}
              onChange={(e) => set('employee_max', e.target.value)} />
          </div>
          <div className="pair">
            <Text label="Revenue — min ($M)" type="number" value={f.revenue_min}
              onChange={(e) => set('revenue_min', e.target.value)} />
            <Text label="Revenue — max ($M)" type="number" value={f.revenue_max}
              onChange={(e) => set('revenue_max', e.target.value)} />
          </div>
          <p className="meta">In millions — enter 5 for $5M.</p>
        </>
      )}

      {step === 3 && (
        <>
          <Text label="City" value={f.cities} placeholder="Nashville, Denver"
            hint="Comma separated. Blank keeps this level open."
            onChange={(e) => set('cities', e.target.value)} />
          <Text label="State" value={f.states} placeholder="TN, CO"
            onChange={(e) => set('states', e.target.value)} />
          <Text label="Region / country" value={f.countries} placeholder="United States"
            onChange={(e) => set('countries', e.target.value)} />
        </>
      )}

      {step === 4 && (
        <Area label="Business needs and pains" rows={10} value={f.pains}
          placeholder="What is failing inside these companies that you fix? Specific enough that you could recognise it from the outside."
          onChange={(v) => set('pains', v)} />
      )}

      {step === 5 && (
        <Area label="Disqualifiers" rows={8} value={f.exclusions}
          hint="Comma separated. One per idea."
          placeholder="Under $5M ARR, Distressed or insolvent, Client conflict"
          onChange={(v) => set('exclusions', v)} />
      )}
    </Drawer>
  );
}

// --- persona form ------------------------------------------------------------

function PersonaForm({ persona, icpId, onClose, onSave }: {
  persona: Per | null; icpId: string | null;
  onClose: () => void; onSave: (d: Record<string, unknown>) => Promise<unknown>;
}) {
  const [f, setF] = useState({
    name: persona?.name ?? '',
    role_kind: persona?.role_kind ?? 'decision_maker',
    titles: list(persona?.titles).join(', '),
    seniorities: list(persona?.seniorities).join(', '),
    responsibilities: persona?.responsibilities ?? '',
    pains: persona?.pains ?? '',
    message_angle: persona?.message_angle ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));

  return (
    <Drawer
      title={persona ? `Edit ${persona.name}` : 'Add a persona'}
      subtitle="Titles are what the contact search actually sends"
      onClose={onClose}
      footer={
        <button className="primary" disabled={saving || !f.name.trim()} onClick={async () => {
          setSaving(true); setError(null);
          try {
            await onSave({
              ...(persona ? {} : { icp_id: icpId }),
              name: f.name.trim(), role_kind: f.role_kind,
              titles: csv(f.titles), seniorities: csv(f.seniorities),
              responsibilities: f.responsibilities || null,
              pains: f.pains || null, message_angle: f.message_angle || null,
            });
            onClose();
          } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e)); setSaving(false);
          }
        }}>{saving ? 'Saving…' : persona ? 'Save' : 'Add persona'}</button>
      }
    >
      <Err>{error}</Err>
      <Text label="Name" value={f.name} autoFocus placeholder="The Operating CEO"
        onChange={(e) => set('name', e.target.value)} />
      <Pick label="Role on the deal" value={f.role_kind} allowEmpty={false}
        options={ROLES.map((v) => ({ value: v, label: v.replace('_', ' ') }))}
        onChange={(v) => set('role_kind', v)} />
      <Text label="Titles" value={f.titles}
        placeholder="CEO, Chief Executive Officer, President, Founder"
        hint="Comma separated. These go straight into the Apollo search — a persona that can't become a query is a poster."
        onChange={(e) => set('titles', e.target.value)} />
      <Text label="Seniorities" value={f.seniorities}
        placeholder="owner, founder, c_suite, vp, director"
        hint="Apollo's vocabulary: owner, founder, c_suite, partner, vp, head, director, manager."
        onChange={(e) => set('seniorities', e.target.value)} />
      <Area label="Job responsibilities" rows={4} value={f.responsibilities}
        placeholder="What are they on the hook for? Not what they want — what they answer for."
        onChange={(v) => set('responsibilities', v)} />
      <Area label="Pains" rows={4} value={f.pains} onChange={(v) => set('pains', v)} />
      <Area label="Message angle" rows={3} value={f.message_angle}
        placeholder="The line that earns a reply from this person specifically."
        onChange={(v) => set('message_angle', v)} />
    </Drawer>
  );
}
